import { Stack, Typography } from "@mui/material";
import {
  Button,
  IconButton,
  LoadingIndicator,
  useFoundationColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DividerComponent } from "../../../../surveys/components/commonQuestionConfigurationDetailsComponents/DividerComponent";
import { observer } from "mobx-react";
import {
  useEmailTemplateDetailsStore,
  useUpdateEmailTemplateStore,
} from "../../../stores/Hooks";
import { UpdateEmailTemplateForm } from "../../forms/UpdateEmailTemplateForm";
import { getValidatedLeoUUIDFromSearchParam } from "../../../../utils/getValidatedLeoUUIDFromSearchParam";
import { useSearchParams } from "react-router-dom";
import {
  RouteSearchParam,
  useRouteNavigator,
} from "../../../../../routes/RoutesHelper";
import { GetEmailTemplateDetailsRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getEmailTemplateDetailsRPC";
import { UserPrivileges } from "../../../../store/user/UserPrivileges";
import { useSignInStore } from "../../../../store/Hooks";
import { UpdateEmailTemplateRPC } from "@pulse/pulse-rpcs/lib/email-cycle/updateEmailTemplateRPC";

interface UpdateEmailTemplateDrawerProps {
  handleDrawerClose: VoidFunction;
  setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenUnsavedChangesDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenSuccessMessageDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenErrorMessageDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenProjectArchivedErrorMessageDialog: React.Dispatch<
    React.SetStateAction<boolean>
  >;
}

export const UpdateEmailTemplateDrawerContent: React.FC<UpdateEmailTemplateDrawerProps> =
  observer(
    ({
      handleDrawerClose,
      setDrawerOpen,
      setOpenUnsavedChangesDialog,
      setOpenSuccessMessageDialog,
      setOpenErrorMessageDialog,
      setOpenProjectArchivedErrorMessageDialog,
    }) => {
      const [loadingEmailTemplateDetails, setLoadingEmailTemplateDetails] =
        useState(false);
      const [searchParams] = useSearchParams();

      const { t } = useTranslation();
      const tokens = useFoundationColorTokens();
      const typography = useTypography();
      const spacing = useSpacing();
      const navigateTo = useRouteNavigator();
      const signInStore = useSignInStore();
      const emailTemplateDetailsStore = useEmailTemplateDetailsStore();
      const updateEmailTemplateStore = useUpdateEmailTemplateStore();

      useEffect(() => {
        const templateId = getValidatedLeoUUIDFromSearchParam(
          searchParams,
          RouteSearchParam.TemplateId,
        );
        if (!templateId) return navigateTo.pageNotFound();

        setLoadingEmailTemplateDetails(true);
        emailTemplateDetailsStore
          .getEmailTemplateDetails(templateId, true)
          .then(() => {
            const userHasPrivilege = signInStore.isPrivilegeGranted(
              UserPrivileges.processGlobalEmailTemplate,
            );
            if (!userHasPrivilege) {
              navigateTo.pageNotFound();
            }
          })
          .finally(() => setLoadingEmailTemplateDetails(false));
      }, []);

      useEffect(() => {
        if (!emailTemplateDetailsStore.getEmailTemplateDetailsRPCError) return;
        switch (emailTemplateDetailsStore.getEmailTemplateDetailsRPCError) {
          case GetEmailTemplateDetailsRPC.RPCError.InvalidTemplateId:
            navigateTo.pageNotFound();
            break;
          default:
            emailTemplateDetailsStore.getEmailTemplateDetailsRPCError satisfies never;
        }
      }, [emailTemplateDetailsStore.getEmailTemplateDetailsRPCError]);

      const handleUpdateEmailTemplate = async (): Promise<void> => {
        try {
          await updateEmailTemplateStore.updateEmailTemplate();
          if (updateEmailTemplateStore.disableSubmit) return;

          switch (updateEmailTemplateStore.updateEmailTemplateRPCError) {
            case null:
              setOpenSuccessMessageDialog(true);
              break;
            case UpdateEmailTemplateRPC.RPCError.InvalidTemplateId:
              navigateTo.pageNotFound();
              break;
            case UpdateEmailTemplateRPC.RPCError.ProjectAlreadyArchived:
              setOpenProjectArchivedErrorMessageDialog(true);
              break;
            default:
              updateEmailTemplateStore.updateEmailTemplateRPCError satisfies never;
          }

          setDrawerOpen(false);
        } catch {
          setDrawerOpen(false);
          setOpenErrorMessageDialog(true);
        }
      };

      return (
        <>
          <Stack
            divider={<DividerComponent orientation="horizontal" width="100%" />}
            height="100%"
          >
            {/* Drawer Header */}
            <Stack
              direction="row"
              paddingBlock={spacing.spaceMD}
              paddingInline={spacing.space2XL}
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography color={tokens.labelHighEmphasis} {...typography.sh2}>
                {t("emailTemplates.editEmailTemplate", "Edit Template")}
              </Typography>
              <IconButton
                name="updateEmailTemplateDrawerCloseButton"
                size="medium"
                icon={<X />}
                variant="plain-neutral"
                onClick={() =>
                  !updateEmailTemplateStore.disableSubmit
                    ? setOpenUnsavedChangesDialog(true)
                    : handleDrawerClose()
                }
              />
            </Stack>

            {/* Drawer Body */}
            {loadingEmailTemplateDetails ? (
              <LoadingIndicator
                variant="container"
                loadingText={t("common.detailsLoadingState")}
                isLoading
              />
            ) : (
              <Stack
                padding={spacing.space2XL}
                flex={1}
                gap={spacing.spaceXL}
                overflow="auto"
              >
                <Typography
                  color={tokens.labelHighEmphasis}
                  {...typography.sh2}
                >
                  {t("emailTemplates.messageCustomization")}
                </Typography>
                <UpdateEmailTemplateForm />
              </Stack>
            )}

            {/* Drawer Footer */}
            <Stack
              alignItems="end"
              paddingBlock={spacing.spaceMD}
              paddingInline={spacing.space2XL}
            >
              <Button
                name="updateEmailTemplateButton"
                label={t("common.saveChanges")}
                variant="filled"
                size="medium"
                onClick={handleUpdateEmailTemplate}
                disabled={updateEmailTemplateStore.disableSubmit}
              />
            </Stack>
          </Stack>

          <LoadingIndicator
            isLoading={updateEmailTemplateStore.loadingUpdateEmailTemplateRPC}
          />
        </>
      );
    },
  );
